import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BASE}/api`;
// Legacy localStorage token — kept read-only so users with an existing
// Bearer token can still call the API on their next visit. New logins are
// tracked via HttpOnly `tf_session` cookie set by the backend.
const LEGACY_TOKEN_KEY = "tf_admin_token";

// withCredentials ensures the HttpOnly session cookie is sent on same-origin
// (and CORS-with-credentials) requests to /api.
const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((cfg) => {
  const legacy = localStorage.getItem(LEGACY_TOKEN_KEY);
  if (legacy) cfg.headers.Authorization = `Bearer ${legacy}`;
  return cfg;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      // Clear any lingering legacy token — cookie is invalidated by the backend.
      localStorage.removeItem(LEGACY_TOKEN_KEY);
    }
    return Promise.reject(err);
  },
);

/**
 * Client-side auth helper. Session state now lives in an HttpOnly cookie
 * that the browser sends automatically — we no longer stash the JWT in
 * localStorage. Auth status is determined by hitting `/auth/me`.
 */
export const auth = {
  /** True if a legacy Bearer token exists locally (backwards compat only). */
  get hasLegacyToken() {
    return Boolean(localStorage.getItem(LEGACY_TOKEN_KEY));
  },
  /**
   * Store legacy token (used by AdminVerify for old backends that only
   * return a JSON token). Kept so preview/testing can log in the same way.
   */
  setToken(t) {
    if (t) localStorage.setItem(LEGACY_TOKEN_KEY, t);
    else localStorage.removeItem(LEGACY_TOKEN_KEY);
  },
  /** Verify with the backend whether the current cookie/token is valid. */
  async me() {
    const r = await api.get("/auth/me");
    return r.data;
  },
  /** Server-side session revocation + local cleanup. */
  async logout() {
    try {
      await api.post("/auth/logout");
    } catch {
      /* fall through: still clear local state */
    }
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  },
};

export default api;
